import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import api from '../../services/api';

/**
 * Cashout Rules Popup (Iter 145)
 * ==============================
 * Player-facing modal that renders admin-edited cashout rules.
 *
 * Display modes (resolved in order):
 *   1. Doc-level `image_url` set  →  single full-bleed image (legacy mode).
 *   2. ANY rule has `image_url`   →  grid of clickable image TILES (4-up).
 *                                    Click a tile → detail view with
 *                                    Next/Prev/Close, walking through every
 *                                    rule in order.
 *   3. Otherwise                  →  vertical bullet list (legacy mode).
 *
 * The tile mode is the default the user requested — admins upload one image
 * per rule from the admin Cashout Rules page; players see four polished
 * cards on screen and tap to drill in.
 *
 * Endpoints:
 *   GET  /api/cashout-rules            global rules
 *   GET  /api/cashout-rules/{gameId}   per-game override fallback to global
 *
 * Tip: while ``overrideDoc`` is provided, the popup skips the network call
 * and renders the doc verbatim — used by the admin Live Preview.
 */
const cleanSource = (source) => (
  String(source || '').toLowerCase() === 'fp' || String(source || '').toLowerCase() === 'freeplay'
    ? 'fp'
    : 'wallet'
);

const pickSourceDoc = (doc, source) => {
  if (!doc) return doc;
  if (cleanSource(source) === 'fp' && doc.freeplay) return doc.freeplay;
  return doc;
};

export default function CashoutRulesPopup({ gameId, onClose, overrideDoc = null, source = 'wallet' }) {
  const rulesSource = cleanSource(source);
  const [doc, setDoc] = useState(pickSourceDoc(overrideDoc, rulesSource));
  const [loading, setLoading] = useState(!overrideDoc);
  const [err, setErr] = useState(null);
  // When set, we're showing rule[detailIdx] in the drill-down view.
  const [detailIdx, setDetailIdx] = useState(null);

  useEffect(() => {
    if (overrideDoc) {
      setDoc(pickSourceDoc(overrideDoc, rulesSource));
      setLoading(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    const params = `?source=${encodeURIComponent(rulesSource)}`;
    const url = gameId ? `/api/cashout-rules/${gameId}${params}` : `/api/cashout-rules${params}`;
    api
      .get(url)
      .then((r) => { if (live) setDoc(r.data); })
      .catch((e) => {
        if (live) setErr(e.response?.data?.message || 'Could not load cashout rules.');
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [gameId, overrideDoc, rulesSource]);

  // Lock scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc closes detail-view first, then the whole popup.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (detailIdx !== null) setDetailIdx(null);
      else onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, detailIdx]);

  // Iter 145+: swipe-left/right on the detail card to navigate rules,
  // mirroring the Prev/Next buttons. Threshold of 50 px and the gesture
  // must finish within 600 ms — anything slower is treated as a scroll.
  const touchRef = useMemo(() => ({ x: 0, y: 0, t: 0, active: false }), []);
  const handleTouchStart = (e) => {
    if (!e.touches?.[0]) return;
    touchRef.x = e.touches[0].clientX;
    touchRef.y = e.touches[0].clientY;
    touchRef.t = Date.now();
    touchRef.active = true;
  };
  const handleTouchEnd = (e) => {
    if (!touchRef.active) return;
    touchRef.active = false;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - touchRef.x;
    const dy = t.clientY - touchRef.y;
    const dt = Date.now() - touchRef.t;
    if (dt > 600) return;
    if (Math.abs(dx) < 50) return;
    if (Math.abs(dy) > Math.abs(dx)) return; // scroll, not swipe
    if (dx < 0) goNext();
    else goPrev();
  };

  const rules = useMemo(() => Array.isArray(doc?.rules) ? doc.rules : [], [doc]);
  // Iter 145+: cap the player popup to the first 4 rules in tile mode so
  // the gallery is always exactly 4 single-column rows. Admin can still
  // edit / store more rules via the admin page; only the first 4 surface
  // in the player UI.
  const TILE_LIMIT = 4;
  const tiles = useMemo(() => rules.slice(0, TILE_LIMIT), [rules]);
  const hasAnyTileImage = useMemo(() => tiles.some(r => !!r?.image_url), [tiles]);
  const useTileMode = !doc?.image_url && hasAnyTileImage && tiles.length > 0;

  const resolveUrl = (u) => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${u}`;
  };

  const goNext = () => {
    if (detailIdx === null) return;
    if (detailIdx + 1 < tiles.length) setDetailIdx(detailIdx + 1);
    else onClose?.();
  };
  const goPrev = () => {
    if (detailIdx === null) return;
    if (detailIdx - 1 >= 0) setDetailIdx(detailIdx - 1);
  };

  const detailRule = detailIdx !== null ? tiles[detailIdx] : null;
  const isLastDetail = detailIdx !== null && detailIdx + 1 >= tiles.length;
  const closeRules = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onClose?.();
  };

  const overlay = (
    <div
      className="fixed inset-0 z-[100001] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={closeRules}
      data-testid="cashout-rules-overlay"
    >
      <div
        className="relative w-full max-w-xl h-[92dvh] sm:h-auto sm:max-h-[92dvh] overflow-hidden rounded-t-3xl sm:rounded-3xl flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #14181f 0%, #0B0E14 100%)',
          border: '1px solid rgba(212,175,55,0.25)',
          boxShadow: '0 -20px 60px rgba(212,175,55,0.15), 0 20px 60px rgba(0,0,0,0.5)',
          WebkitOverflowScrolling: 'touch',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="cashout-rules-popup"
      >
        {/* Header */}
        <div
          className="shrink-0 z-10 flex items-start justify-between gap-3 px-6 pt-6 pb-4"
          style={{
            background: 'linear-gradient(180deg, rgba(20,24,31,1) 60%, rgba(20,24,31,0.7) 100%)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
                boxShadow: '0 6px 24px rgba(212,175,55,0.35)',
              }}
            >
              <Sparkles className="w-6 h-6 text-black" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300/70 font-semibold">
                Cashout Rules
              </p>
              <h2
                className="text-2xl sm:text-3xl font-black text-white leading-tight mt-0.5"
                data-testid="cashout-rules-headline"
              >
                {doc?.headline || 'Cashout Rules'}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onPointerDown={closeRules}
            onClick={closeRules}
            className="shrink-0 p-2 rounded-full hover:bg-white/10 transition"
            data-testid="cashout-rules-close"
            aria-label="Close cashout rules"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div
          className="px-6 pb-8 overflow-y-auto flex-1 min-h-0 overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-7 h-7" />
            </div>
          )}
          {err && !loading && (
            <p className="text-red-400 text-base py-6 text-center">{err}</p>
          )}

          {/* Mode 1: doc-level image (legacy single image) */}
          {!loading && !err && doc && doc.image_url && (
            <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              <img
                src={resolveUrl(doc.image_url)}
                alt={doc.headline || 'Cashout Rules'}
                className="w-full h-auto block"
                data-testid="cashout-rules-image"
              />
              {doc.footer && (
                <p
                  className="text-sm text-gray-400 italic leading-relaxed mt-6 text-center"
                  data-testid="cashout-rules-footer"
                >
                  {doc.footer}
                </p>
              )}
            </div>
          )}

          {/* Mode 2: per-rule image TILES (Iter 145) */}
          {!loading && !err && doc && !doc.image_url && useTileMode && detailIdx === null && (
            <>
              {doc.intro && (
                <p
                  className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5"
                  data-testid="cashout-rules-intro"
                >
                  {doc.intro}
                </p>
              )}
              <div
                className="flex flex-col gap-3"
                data-testid="cashout-rules-tiles"
              >
                {tiles.map((r, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setDetailIdx(idx)}
                    className="text-left rounded-3xl overflow-hidden transition active:scale-[0.98] hover:brightness-110 relative group flex"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(212,175,55,0.30)',
                      boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                    }}
                    data-testid={`cashout-rules-tile-${idx}`}
                  >
                    <div
                      className="w-28 sm:w-32 shrink-0 flex items-center justify-center"
                      style={{
                        background: r.image_url
                          ? `url("${resolveUrl(r.image_url)}") center/cover no-repeat`
                          : 'linear-gradient(135deg, rgba(212,175,55,0.20), rgba(212,175,55,0.04))',
                      }}
                    >
                      {!r.image_url && (
                        <div
                          className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl my-6"
                          style={{
                            background: 'rgba(212,175,55,0.16)',
                            border: '1px solid rgba(212,175,55,0.4)',
                          }}
                        >
                          {r.icon || '✨'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 px-4 py-3 flex flex-col justify-center min-w-0">
                      <p
                        className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-0.5"
                        style={{ color: 'rgba(245,215,110,0.85)' }}
                      >
                        Rule {idx + 1}
                      </p>
                      <p className="text-base sm:text-lg font-bold text-white leading-tight line-clamp-2">
                        {r.title}
                      </p>
                    </div>
                    <div className="flex items-center pr-4 shrink-0" style={{ color: 'rgba(245,215,110,0.85)' }}>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </button>
                ))}
              </div>
              {doc.footer && (
                <p
                  className="text-sm text-gray-400 italic leading-relaxed mt-6 text-center"
                  data-testid="cashout-rules-footer"
                >
                  {doc.footer}
                </p>
              )}
            </>
          )}

          {/* Mode 2b: TILE drill-down detail with Next/Prev nav (Iter 145) */}
          {!loading && !err && useTileMode && detailRule && (
            <div
              data-testid="cashout-rules-detail"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              className="touch-pan-y"
            >
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setDetailIdx(null)}
                  className="text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5"
                  style={{ background: 'rgba(255,255,255,0.06)', color: '#9CA3AF' }}
                  data-testid="cashout-rules-detail-back"
                >
                  <ChevronLeft className="w-4 h-4" /> All rules
                </button>
                <span
                  className="text-[10px] uppercase tracking-[0.2em] font-bold"
                  style={{ color: 'rgba(245,215,110,0.85)' }}
                  data-testid="cashout-rules-detail-counter"
                >
                  {detailIdx + 1} of {tiles.length}
                </span>
              </div>
              {detailRule.image_url && (
                <div
                  className="rounded-3xl overflow-hidden mb-4"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <img
                    src={resolveUrl(detailRule.image_url)}
                    alt={detailRule.title}
                    className="w-full h-auto block"
                  />
                </div>
              )}
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
                  style={{
                    background: 'rgba(212,175,55,0.14)',
                    border: '1px solid rgba(212,175,55,0.3)',
                  }}
                >
                  {detailRule.icon || '✨'}
                </div>
                <h3
                  className="text-xl sm:text-2xl font-extrabold text-white leading-tight pt-1"
                  data-testid="cashout-rules-detail-title"
                >
                  {detailRule.title}
                </h3>
              </div>
              <p
                className="text-sm sm:text-base text-gray-300 leading-relaxed"
                data-testid="cashout-rules-detail-body"
              >
                {detailRule.body}
              </p>
              {/* Subtle swipe hint — only on touch viewports */}
              <p
                className="text-[10px] uppercase tracking-[0.2em] text-center mt-5 sm:hidden"
                style={{ color: 'rgba(255,255,255,0.35)' }}
                data-testid="cashout-rules-swipe-hint"
              >
                ← swipe&nbsp;&nbsp;|&nbsp;&nbsp;swipe →
              </p>
            </div>
          )}

          {/* Mode 3: legacy bullet list (no per-rule images) */}
          {!loading && !err && doc && !doc.image_url && !useTileMode && (
            <>
              {doc.intro && (
                <p
                  className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6"
                  data-testid="cashout-rules-intro"
                >
                  {doc.intro}
                </p>
              )}
              <ul className="space-y-3">
                {(doc.rules || []).map((r, idx) => (
                  <li
                    key={idx}
                    className="rounded-2xl p-4 sm:p-5"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                    data-testid={`cashout-rule-${idx}`}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div
                        className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-2xl"
                        style={{
                          background: 'rgba(212,175,55,0.12)',
                          border: '1px solid rgba(212,175,55,0.25)',
                        }}
                      >
                        {r.icon || '•'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-white leading-snug mb-1.5">
                          {r.title}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                          {r.body}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {doc.footer && (
                <p
                  className="text-sm text-gray-400 italic leading-relaxed mt-6 text-center"
                  data-testid="cashout-rules-footer"
                >
                  {doc.footer}
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer CTA */}
        <div
          className="shrink-0 px-6 py-4 border-t flex items-center gap-3"
          style={{
            background: 'rgba(20,24,31,0.95)',
            borderColor: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(8px)',
          }}
        >
          {/* Detail-mode shows Prev + Next */}
          {useTileMode && detailIdx !== null ? (
            <>
              <button
                onClick={goPrev}
                disabled={detailIdx === 0}
                className="px-4 py-3 rounded-2xl text-sm font-bold transition disabled:opacity-40 flex items-center gap-2"
                style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }}
                data-testid="cashout-rules-prev"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <button
                onClick={goNext}
                className="flex-1 py-4 rounded-2xl text-base font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
                  color: '#000',
                  boxShadow: '0 8px 24px rgba(212,175,55,0.25)',
                }}
                data-testid="cashout-rules-next"
              >
                {isLastDetail ? (
                  <>I agree, let&apos;s play</>
                ) : (
                  <>Next <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              onPointerDown={closeRules}
              onClick={closeRules}
              className="w-full py-4 rounded-2xl text-base font-bold transition-all hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
                color: '#000',
                boxShadow: '0 8px 24px rgba(212,175,55,0.25)',
              }}
              data-testid="cashout-rules-acknowledge"
            >
              I agree, let&apos;s play
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const host = typeof document !== 'undefined' ? document.body : null;
  return host ? createPortal(overlay, host) : overlay;
}
